import React from 'react';
import {
	type FieldPath,
	type FieldValues,
	type PathValue,
} from 'react-hook-form';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {Switch, type SwitchProps} from '@netx/core-components/Switch';
import {splitEventHandler} from '@netx/core-next/utils';
import {combineRefs} from '@netx/core-next/utils/react';
import {
	ReactHookFormControl,
	type WithReactHookFormControlFieldProps,
} from '../ReactHookFormControl';

export interface ReactHookFormSwitchProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends WithReactHookFormControlFieldProps<
		Omit<SwitchProps, 'checked'>,
		TFieldValues,
		TName
	> {
	/** hook for parsing the value from the form to the input */
	parseValueIn?: (value: PathValue<TFieldValues, TName>) => boolean;
	/** hook for parsing the value from the input to the form */
	parseValueOut?: (value: boolean) => PathValue<TFieldValues, TName>;
}

export const ReactHookFormSwitchPrimitive = <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	{
		control,
		parseValueIn,
		parseValueOut,
		ControllerProps,
		...props
	}: ReactHookFormSwitchProps<TFieldValues, TName>,
	ref: any,
) => {
	return (
		<ReactHookFormControl<TFieldValues, TName>
			{...ControllerProps}
			control={control}
			name={props.name as TName}
			render={({field, formState}) => (
				<Switch
					// TODO: we are not reflecting the error in the ui atm
					// error={formState.errors[props.name]?.message?.toString()}
					{...props}
					{...field}
					value={parseValueIn ? parseValueIn(field.value) : field.value}
					disabled={props.disabled || field.disabled}
					ref={ref}
					inputRef={combineRefs(props.inputRef, field.ref)}
					onChange={useConstCallback(
						splitEventHandler(props.onChange, (event, checked) => {
							field.onChange(parseValueOut ? parseValueOut(checked) : checked);
						}),
					)}
				/>
			)}
			rules={{
				required: props.required,
				...ControllerProps?.rules,
			}}
		/>
	);
};

/**
 * `react-hook-form` controlled `Switch` component
 */
export const ReactHookFormSwitch = React.forwardRef(
	ReactHookFormSwitchPrimitive,
) as <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	props: ReactHookFormSwitchProps<TFieldValues, TName>,
) => JSX.Element;

export default ReactHookFormSwitch;
